import React, { forwardRef, useImperativeHandle, useMemo, useEffect, useRef, useLayoutEffect } from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import classNames from 'classnames';
import PaginationAlgo from './Pagination';
import Facade from '../Facade';
import Loader from '../Loader';
import PageIndicator from './PageIndicator';
import SelectPerpage from './SelectPerPage';
import Table from './Table';
import { GridContext } from './hook';
import { useRememberTableState } from './RememberTableState';
import Graph from './Graph';
import Filter from './Filter';
import { useGridSwitching } from '../../common/hooks/useGridSwitching';
import { useTable } from '../../common/hooks/useTable';
import './index.scss';
;
;
//@ts-ignore
const Grid = /*#__PURE__*/forwardRef(({
  name,
  columns: columnsRaw,
  actions,
  className,
  rowAttr,
  collapsible,
  collapseRender,
  draggable,
  defaultPage,
  defaultPerPage,
  defaultFilter,
  defaultSort,
  headerSection,
  showFooter,
  sortIcon,
  loading,
  facadeLoading,
  data,
  totalCount,
  totalCountAsPerPage,
  onChange,
  perPageOptions,
  children
}, ref) => {
  const [tableData, setTableData] = useRememberTableState(name || "");
  const tableProps = useTable({
    page: tableData.page || defaultPage,
    perPage: tableData.perPage || defaultPerPage,
    filters: tableData.filters || defaultFilter,
    sort: tableData.sort || defaultSort
  });
  const {
    page,
    perPage,
    filters,
    sort
  } = tableProps;
  const unmounted = useRef(false);
  useGridSwitching({
    dataKey: 'data-id',
    orderFieldName: 'ListOrder',
    ...draggable,
    droppable: `[data-${draggable?.droppable || 'droppable'}-container="true"]`,
    draggable: `[data-${draggable?.draggable || 'draggable'}-item="true"]`
  });
  useLayoutEffect(() => {
    if (onChange) {
      onChange(tableProps);
    } // eslint-disable-next-line

  }, [page, sort, perPage, filters]);
  useEffect(() => {
    if (totalCountAsPerPage) {
      tableProps.setPerPage(totalCount);
    }
  }, [totalCount]);
  useImperativeHandle(ref, () => ({
    page,
    perPage,
    sort,
    filters,
    setFilters: tableProps.setFilters,
    setPage: tableProps.setPage,
    setPerPage: tableProps.setPerPage,
    setSort: tableProps.setSort,
    reload: () => {
      if (onChange) {
        onChange(tableProps);
      }
    },
    deleteReload: () => {
      if (onChange) {
        onChange(tableProps);
      }
    } // eslint-disable-next-line

  }), [page, perPage, sort, filters]);
  const columns = useMemo(() => actions ? [...columnsRaw, {
    label: 'Actions',
    dataIndex: '',
    render: (_, row, index, states) => {
      return /*#__PURE__*/React.createElement("div", {
        className: "action-btns"
      }, actions.map(({
        icon,
        title,
        className,
        onClick,
        showWhen,
        children,
        stopPropagation = true
      }, key) => (!showWhen || showWhen(row, index, states)) && (title ? /*#__PURE__*/React.createElement(OverlayTrigger, {
        key: key,
        overlay: /*#__PURE__*/React.createElement(Tooltip, {
          id: (title || '') + index
        }, title)
      }, /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: classNames("btn btn-sm btn-clean btn-icon", null, className),
        onClick: event => {
          if (stopPropagation) {
            event.stopPropagation();
          }

          onClick(event, row);
        }
      }, children || /*#__PURE__*/React.createElement("i", {
        className: icon
      }))) : /*#__PURE__*/React.createElement("button", {
        key: key,
        type: "button",
        className: classNames("btn btn-sm btn-clean btn-icon", null, className),
        onClick: event => {
          if (stopPropagation) {
            event.stopPropagation();
          }

          onClick(event, row);
        }
      }, children || /*#__PURE__*/React.createElement("i", {
        className: icon
      })))));
    }
  } // eslint-disable-next-line
  ] : columnsRaw, [actions, columnsRaw]);
  useEffect(() => {
    return () => {
      unmounted.current = true;
    };
  }, []);
  useEffect(() => {
    return () => {
      if (unmounted.current && name) {
        setTableData({
          page,
          perPage,
          sort,
          filters
        });
      }
    };
  }, [page, perPage, sort, filters]);
  const isDraggable = !!draggable?.container;
  return /*#__PURE__*/React.createElement(GridContext.Provider, {
    value: { ...tableProps,
      columns,
      loading: !!loading,
      actions,
      rowAttr,
      collapsible,
      collapseRender,
      draggable,
      isDraggable,
      query: {
        data,
        totalCount,
        loading: !!facadeLoading
      }
    }
  }, children || /*#__PURE__*/React.createElement("div", {
    id: draggable?.container,
    className: "grid-table grid-switching"
  }, headerSection, /*#__PURE__*/React.createElement("div", {
    className: "grid-content"
  }, /*#__PURE__*/React.createElement(Table, {
    sortIcon: sortIcon,
    className: classNames('table', null, className)
  })), showFooter && /*#__PURE__*/React.createElement("div", {
    className: "grid-footer"
  }, /*#__PURE__*/React.createElement(Facade, {
    loading: !!facadeLoading && !totalCount,
    rows: 1,
    range: [20, 20],
    padding: false,
    style: {
      flex: '0.7'
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "grid-pageinfo"
  }, /*#__PURE__*/React.createElement(SelectPerpage, {
    options: perPageOptions
  }), /*#__PURE__*/React.createElement(PageIndicator, null))), /*#__PURE__*/React.createElement(Facade, {
    loading: !!facadeLoading && !totalCount,
    rows: 1,
    padding: false,
    style: {
      flex: '0.3'
    }
  }, /*#__PURE__*/React.createElement(PaginationAlgo, {
    size: "lg"
  }))), /*#__PURE__*/React.createElement(Loader, {
    active: !!loading
  })));
});
Grid.defaultProps = {
  className: 'table-head-custom table-head-bg table-vertical-center table-zebra table-hover',
  sortIcon: /*#__PURE__*/React.createElement("svg", {
    height: "20",
    width: "20",
    viewBox: "0 0 20 20",
    "aria-hidden": "true",
    focusable: "false",
    className: "css-tj5bde-Svg"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"
  })),
  showFooter: true,
  perPageOptions: [10, 20, 50, 100, 250]
};
Grid.Filter = Filter;
Grid.SelectPerPage = SelectPerpage;
Grid.Pagination = PaginationAlgo;
Grid.PageIndicator = PageIndicator;
Grid.Table = Table;
Grid.Graph = Graph;
export default Grid;